import { useState, useEffect } from 'react';
import { Plane, Shield, Award, Users, Globe2, Building2, Phone, Mail, MapPin, ChevronRight, Menu, X, Check, Clock, Star, Briefcase, Heart, Mountain, Ship, GraduationCap, Hotel, FileText, Calendar } from 'lucide-react';
import './App.css';

function App() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const services = [
    {
      icon: Plane,
      title: "Airline Reservations & Ticketing",
      description: "Local and international flight booking with competitive fares and 24/7 support",
      features: ["IATA Licensed", "Global Coverage", "Instant Confirmation"]
    },
    {
      icon: Mountain,
      title: "Religious Pilgrimages",
      description: "Sacred journeys to holy lands with expert spiritual guides and comprehensive tour packages",
      features: ["Mount Tabor", "Dead Sea", "Biblical Sites"]
    },
    {
      icon: Briefcase,
      title: "Corporate Travel Management",
      description: "Tailored business travel solutions for organizations, government agencies, and NGOs",
      features: ["Bulk Bookings", "Travel Policy", "Cost Management"]
    },
    {
      icon: Ship,
      title: "Luxury Safari Tours",
      description: "Immersive African wilderness experiences with premium accommodations and expert guides",
      features: ["Wildlife Tours", "Luxury Lodges", "Cultural Experiences"]
    },
    {
      icon: Heart,
      title: "Holiday & Honeymoon Packages",
      description: "Romantic getaways and all-inclusive resort stays designed for unforgettable experiences",
      features: ["Beach Resorts", "Wine Tasting", "Spa Treatments"]
    },
    {
      icon: Shield,
      title: "Travel Insurance",
      description: "Comprehensive coverage for cancellations, medical emergencies, and lost luggage",
      features: ["Medical Coverage", "Cancellation Protection", "24/7 Assistance"]
    },
    {
      icon: FileText,
      title: "Visa Procurement",
      description: "Expert assistance with visa applications and documentation, specializing in Dubai visas",
      features: ["Dubai Visa", "Document Support", "Fast Processing"]
    },
    {
      icon: GraduationCap,
      title: "Medical & Study Tours",
      description: "Medical tourism arrangements and educational study tours for institutions",
      features: ["Medical Appointments", "Educational Tours", "Group Arrangements"]
    }
  ];

  const majorClients = [
    "Nigerian National Petroleum Corporation (NNPC) - COMD",
    "NNPC - NAPIMS Division",
    "NNPC - NGMC Division",
    "NNPC Pension Fund Limited",
    "Police Service Commission",
    "Nigerian Christian Pilgrims Commission",
    "MercyCorps International",
    "Oxfam International",
    "Heartland Alliance",
    "Chemonics International"
  ];

  const airlines = {
    international: ["British Airways", "Lufthansa", "Emirates", "Air France", "Ethiopian Airlines", "Virgin Atlantic", "South African Airways", "Kenya Airways", "Egypt Air", "Air Maroc"],
    local: ["Arik Air", "Air Peace", "Dana Air", "Max Air", "Azman Air", "Overland Airways", "Aero Contractors", "Green Africa"]
  };

  return (
    <div className="professional-site">
      {/* Navigation */}
      <nav className={`main-nav ${scrollY > 50 ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-content">
            <div className="logo-section">
              <div className="logo-icon">
                <Plane className="plane-icon" />
              </div>
              <div className="logo-text">
                <h1>ANZORA TRAVELS</h1>
                <p>& Tours Limited</p>
              </div>
            </div>
            
            <div className="nav-links desktop-nav">
              <a href="#about">About</a>
              <a href="#services">Services</a>
              <a href="#clients">Our Clients</a>
              <a href="#contact">Contact</a>
              <a href="#contact" className="btn-primary">Request Quote</a>
            </div>
            
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <a href="#about" onClick={() => setMobileMenuOpen(false)}>About</a>
            <a href="#services" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#clients" onClick={() => setMobileMenuOpen(false)}>Our Clients</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <a href="#contact" className="btn-primary" onClick={() => setMobileMenuOpen(false)}>Request Quote</a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
          <img 
            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80" 
            alt="Professional Travel Services"
          />
        </div>
        <div className="container hero-content">
          <div className="hero-badge">
            <Shield className="badge-icon" />
            <span>IATA Licensed • RC: 1093042 • Established 2013</span>
          </div>
          <h1 className="hero-title">Excellence in Corporate & <br/>Diplomatic Travel Services</h1>
          <p className="hero-subtitle">
            Trusted by NNPC, government agencies, and international NGOs for over a decade.<br/>
            Professional travel management with 24/7 support and global airline partnerships.
          </p>
          <div className="hero-cta">
            <a href="#contact" className="btn-primary large">
              Request a Consultation
              <ChevronRight className="btn-icon" />
            </a>
            <a href="#services" className="btn-secondary large">
              Our Services
            </a>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <Award className="stat-icon" />
              <div>
                <strong>10+ Years</strong>
                <span>Industry Experience</span>
              </div>
            </div>
            <div className="stat">
              <Users className="stat-icon" />
              <div>
                <strong>50+ Clients</strong>
                <span>Corporate & Government</span>
              </div>
            </div>
            <div className="stat">
              <Globe2 className="stat-icon" />
              <div>
                <strong>18+ Airlines</strong>
                <span>Global Partnerships</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Who We Are</span>
            <h2>Trusted Travel Partner Since 2013</h2>
            <p className="section-intro">
              Anzora Travels & Tours Limited is a fully registered, IATA-licensed travel agency based in the heart of Garki II, Abuja. 
              We specialize in providing comprehensive travel solutions to corporations, government agencies, diplomatic missions, and NGOs across Nigeria and beyond.
            </p>
          </div>

          <div className="credentials-grid">
            <div className="credential-card">
              <div className="credential-icon">
                <Shield />
              </div>
              <h3>IATA Licensed</h3>
              <p>Certified international travel agency with full accreditation from the International Air Transport Association</p>
            </div>
            
            <div className="credential-card">
              <div className="credential-icon">
                <Building2 />
              </div>
              <h3>CAC Registered</h3>
              <p>Duly registered with Corporate Affairs Commission, Abuja (RC: 1093042) since 2013</p>
            </div>
            
            <div className="credential-card">
              <div className="credential-icon">
                <Award />
              </div>
              <h3>Tour Operator License</h3>
              <p>Authorized tour operator for Nigerian Christian Pilgrims Commission</p>
            </div>
            
            <div className="credential-card">
              <div className="credential-icon">
                <Clock />
              </div>
              <h3>24/7 Support</h3>
              <p>Round-the-clock customer service and travel emergency assistance</p>
            </div>
          </div>

          <div className="leadership-section">
            <div className="leadership-content">
              <h3>Led by Experienced Professionals</h3>
              <p>
                Under the leadership of <strong>Managing Director Okeke-Ozobu Ogochukwu Frances</strong>, our team of experienced 
                travel consultants delivers exceptional service across all aspects of corporate and leisure travel. We maintain 
                strong partnerships with major international and local airlines, ensuring competitive fares and reliable service.
              </p>
              <div className="core-values">
                <div className="value-item">
                  <Check className="check-icon" />
                  <span>Excellence in Service Delivery</span>
                </div>
                <div className="value-item">
                  <Check className="check-icon" />
                  <span>Professional Integrity</span>
                </div>
                <div className="value-item">
                  <Check className="check-icon" />
                  <span>Client-Focused Solutions</span>
                </div>
                <div className="value-item">
                  <Check className="check-icon" />
                  <span>Innovation & Technology</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Our Services</span>
            <h2>Comprehensive Travel Solutions</h2>
            <p className="section-intro">
              From airline reservations to religious pilgrimages, corporate travel management to luxury safari tours, 
              we provide end-to-end travel services tailored to your specific requirements.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  <service.icon />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, i) => (
                    <li key={i}>
                      <Check className="feature-check" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="additional-services">
            <h3>Additional Services</h3>
            <div className="additional-grid">
              <div className="additional-item"><Hotel /> Worldwide Hotel Booking</div>
              <div className="additional-item"><Calendar /> Travel Itinerary Management</div>
              <div className="additional-item"><Briefcase /> Airport Transfers & Car Hire</div>
              <div className="additional-item"><FileText /> Travel Consultancy</div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="clients-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Our Clients</span>
            <h2>Trusted by Leading Organizations</h2>
            <p className="section-intro">
              We have the privilege of serving some of Nigeria's most prestigious corporations, 
              government agencies, and international NGOs.
            </p>
          </div>

          <div className="clients-showcase">
            <h3>Major Corporate & Government Clients</h3>
            <div className="clients-grid">
              {majorClients.map((client, index) => (
                <div key={index} className="client-item">
                  <Building2 className="client-icon" />
                  <span>{client}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="airlines-section">
            <div className="airlines-column">
              <h3>International Airlines</h3>
              <div className="airline-list">
                {airlines.international.map((airline, index) => (
                  <div key={index} className="airline-item">
                    <Plane className="airline-icon" />
                    {airline}
                  </div>
                ))}
              </div>
            </div>
            <div className="airlines-column">
              <h3>Local Airlines</h3>
              <div className="airline-list">
                {airlines.local.map((airline, index) => (
                  <div key={index} className="airline-item">
                    <Plane className="airline-icon" />
                    {airline}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Gallery</span>
            <h2>Travel Experiences</h2>
            <p className="section-intro">
              Explore the destinations we've helped our clients discover
            </p>
          </div>

          <div className="gallery-grid">
            <div className="gallery-item large">
              <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80" alt="Dubai skyline" />
              <div className="gallery-overlay">
                <h3>Dubai</h3>
                <p>Modern luxury and business hub</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?w=600&q=80" alt="Jerusalem" />
              <div className="gallery-overlay">
                <h3>Jerusalem</h3>
                <p>Sacred pilgrimages</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600&q=80" alt="African Safari" />
              <div className="gallery-overlay">
                <h3>Safari Tours</h3>
                <p>African wilderness</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80" alt="Paris" />
              <div className="gallery-overlay">
                <h3>Paris</h3>
                <p>European elegance</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=600&q=80" alt="Maldives" />
              <div className="gallery-overlay">
                <h3>Maldives</h3>
                <p>Beach paradise</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1549693578-d683be217e58?w=600&q=80" alt="Mecca" />
              <div className="gallery-overlay">
                <h3>Holy Lands</h3>
                <p>Religious pilgrimages</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Get In Touch</span>
            <h2>Contact Our Team</h2>
            <p className="section-intro">
              Located in Garki II, Abuja. Reach out for consultations, bookings, or inquiries.
            </p>
          </div>

          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon">
                <Phone />
              </div>
              <h3>Call Us</h3>
              <p>24/7 Customer Support</p>
              <a href="tel:+234" className="contact-link">Contact for Phone Number</a>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <Mail />
              </div>
              <h3>Email Us</h3>
              <p>Quick Response Guaranteed</p>
              <a href="mailto:info@anzoratravels.com" className="contact-link">info@anzoratravels.com</a>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <MapPin />
              </div>
              <h3>Visit Our Office</h3>
              <p>Garki II District</p>
              <span className="contact-link">Abuja, Nigeria</span>
            </div>
          </div>

          <div className="cta-box">
            <h3>Ready to Plan Your Next Journey?</h3>
            <p>Request a consultation or quote from our experienced travel consultants</p>
            <a href="mailto:info@anzoratravels.com" className="btn-primary large">
              Request a Quote
              <ChevronRight className="btn-icon" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="main-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo-section">
                <div className="logo-icon">
                  <Plane className="plane-icon" />
                </div>
                <div className="logo-text">
                  <h3>ANZORA TRAVELS</h3>
                  <p>& Tours Limited</p>
                </div>
              </div>
              <p className="footer-tagline">
                Excellence in Corporate & Diplomatic Travel Services
              </p>
              <div className="footer-credentials">
                <span>RC: 1093042</span>
                <span>•</span>
                <span>IATA Licensed</span>
                <span>•</span>
                <span>Est. 2013</span>
              </div>
            </div>

            <div className="footer-info">
              <h4>Contact Information</h4>
              <div className="footer-contact">
                <MapPin className="footer-icon" />
                <span>Garki II, Abuja, Nigeria</span>
              </div>
              <div className="footer-contact">
                <Mail className="footer-icon" />
                <span>info@anzoratravels.com</span>
              </div>
              <div className="footer-contact">
                <Clock className="footer-icon" />
                <span>24/7 Customer Support</span>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 Anzora Travels & Tours Limited. All rights reserved.</p>
            <p>Registered with Corporate Affairs Commission, Abuja • Creating exceptional travel experiences</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;